/* Signature - recently viewed products. Stores product handles only and loads
   cards from direct product section endpoints; no storefront search is used. */
(function (global) {
  'use strict';

  const KEY = 'sig:recently-viewed';
  const MAX = 12;
  const HANDLE = /^[a-z0-9][a-z0-9-]*$/;

  function normalize(values) {
    const seen = new Set();
    const result = [];
    if (!Array.isArray(values)) return result;

    for (const value of values) {
      if (typeof value !== 'string' || !HANDLE.test(value) || seen.has(value)) continue;
      seen.add(value);
      result.push(value);
      if (result.length >= MAX) break;
    }
    return result;
  }

  function write(storage, handles) {
    try {
      storage.setItem(KEY, JSON.stringify(normalize(handles)));
      return true;
    } catch (error) {
      return false;
    }
  }

  function read(storage) {
    try {
      const raw = storage.getItem(KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const handles = normalize(parsed);
      if (JSON.stringify(parsed) !== JSON.stringify(handles)) write(storage, handles);
      return handles;
    } catch (error) {
      return [];
    }
  }

  function record(handle, handles) {
    if (typeof handle !== 'string' || !HANDLE.test(handle)) return normalize(handles);
    return [handle, ...normalize(handles).filter((value) => value !== handle)].slice(0, MAX);
  }

  async function fetchItem(handle, baseUrl, request, parse) {
    try {
      const response = await request(
        baseUrl + encodeURIComponent(handle) + '?section_id=recently-viewed-product',
        { headers: { Accept: 'text/html' } }
      );
      if (!response.ok) {
        if (response.status === 404 || response.status === 410) {
          try {
            const productResponse = await request(
              baseUrl + encodeURIComponent(handle) + '.js',
              { headers: { Accept: 'application/json' } }
            );
            if (productResponse.status === 404 || productResponse.status === 410) {
              return { handle, status: 'invalid', item: null };
            }
          } catch (error) {
            /* A failed verification is transient and must not erase history. */
          }
        }
        return { handle, status: 'transient', item: null };
      }
      const rendered = parse(await response.text());
      const result = rendered.querySelector('[data-recent-result]');
      if (
        result &&
        result.getAttribute('data-product-handle') === handle &&
        result.getAttribute('data-result-status') === 'unavailable'
      ) {
        return { handle, status: 'invalid', item: null };
      }
      const item = rendered.querySelector('[data-recent-item]');
      if (!item || item.getAttribute('data-product-handle') !== handle) {
        return { handle, status: 'transient', item: null };
      }
      return { handle, status: 'valid', item };
    } catch (error) {
      return { handle, status: 'transient', item: null };
    }
  }

  async function load(container, dependencies) {
    const deps = dependencies || {};
    const request = deps.fetch || global.fetch;
    const parse = deps.parse || ((html) =>
      new DOMParser().parseFromString(html, 'text/html'));
    const storage = deps.storage || global.localStorage;
    const currentHandle = deps.currentHandle || '';
    const grid = container.querySelector('[data-recent-grid]');
    const limit = Math.max(1, Number(container.getAttribute('data-limit')) || 4);
    const baseUrl = container.getAttribute('data-products-url') || '/products/';
    const handles = read(storage).filter((handle) => handle !== currentHandle);

    container.hidden = true;
    if (grid) grid.replaceChildren();
    if (!grid || !handles.length) return false;

    const results = await Promise.all(
      handles.map((handle) => fetchItem(handle, baseUrl, request, parse))
    );
    const invalidHandles = new Set(
      results.filter((result) => result.status === 'invalid').map((result) => result.handle)
    );
    if (invalidHandles.size) {
      write(storage, read(storage).filter((handle) => !invalidHandles.has(handle)));
    }
    const items = results
      .filter((result) => result.status === 'valid')
      .map((result) => result.item)
      .slice(0, limit);
    if (!items.length) return false;

    grid.replaceChildren(...items);
    container.hidden = false;
    return true;
  }

  function init(scope, dependencies) {
    const deps = dependencies || {};
    let storage = deps.storage;
    try {
      storage = storage || global.localStorage;
    } catch (error) {
      return;
    }
    if (!storage) return;
    const current = scope.querySelector('[data-current-product-handle]');
    const currentHandle = current
      ? current.getAttribute('data-current-product-handle') || ''
      : '';

    if (currentHandle) write(storage, record(currentHandle, read(storage)));

    scope.querySelectorAll('[data-recently-viewed]').forEach((container) => {
      if (container.hasAttribute('data-recent-loading')) return;
      container.setAttribute('data-recent-loading', '');
      load(container, { ...deps, storage, currentHandle });
    });
  }

  global.SignatureRecentlyViewed = {
    KEY,
    fetchItem,
    init,
    load,
    normalize,
    read,
    record,
    write,
  };
  if (global.document) init(global.document);
})(typeof window === 'undefined' ? globalThis : window);
